import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { HERO_SLIDES } from '../data/partners';
import { mediaUrl } from '../utils/mediaUrl';
import './Hero.css';

const SLIDE_MS = 4500;
const SWIPE_MIN = 40;

const Hero = ({ onContactClick }) => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const touchStart = useRef(null);
  const videoRefs = useRef({});
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useReducedMotion();
  const len = HERO_SLIDES.length;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], reduceMotion ? [0, 0] : [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], reduceMotion ? [1, 1] : [1, 0.35]);

  const goRelative = useCallback(
    (delta) => {
      setIndex((prev) => (prev + delta + len) % len);
    },
    [len]
  );

  const goAbsolute = useCallback(
    (i) => {
      setIndex(((i % len) + len) % len);
    },
    [len]
  );

  // Mount only active + next to avoid mobile memory / bandwidth failures
  const mountedIds = useMemo(() => {
    const ids = new Set([HERO_SLIDES[index].id]);
    ids.add(HERO_SLIDES[(index + 1) % len].id);
    return ids;
  }, [index, len]);

  useEffect(() => {
    if (paused || reduceMotion) return undefined;
    const t = setInterval(() => {
      setIndex((prev) => (prev + 1) % len);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [paused, reduceMotion, len]);

  useEffect(() => {
    HERO_SLIDES.forEach((slide, i) => {
      const el = videoRefs.current[slide.id];
      if (!el) return;
      if (i === index) {
        const play = () => {
          el.muted = true;
          el.playsInline = true;
          el.play().catch(() => {});
        };
        if (el.readyState >= 2) {
          play();
        } else {
          const onReady = () => {
            play();
            el.removeEventListener('loadeddata', onReady);
          };
          el.addEventListener('loadeddata', onReady);
          el.load();
        }
      } else {
        el.pause();
      }
    });
  }, [index, mountedIds]);

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
    setPaused(true);
  };

  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    setPaused(false);
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
    goRelative(dx < 0 ? 1 : -1);
  };

  const active = HERO_SLIDES[index];

  return (
    <section
      id="home"
      className="hero"
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <motion.div className="hero-media" style={{ y, opacity }} aria-hidden="true">
        {HERO_SLIDES.map((slide, i) => {
          const isActive = i === index;
          const shouldMount = mountedIds.has(slide.id);
          const poster = mediaUrl(slide.poster);
          return (
            <div
              key={slide.id}
              className={`hero-video ${isActive ? 'is-active' : ''}`}
              style={{
                zIndex: isActive ? 2 : 1,
                backgroundImage: !shouldMount ? `url(${poster})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {shouldMount && (
                <video
                  ref={(el) => {
                    if (el) videoRefs.current[slide.id] = el;
                    else delete videoRefs.current[slide.id];
                  }}
                  muted
                  defaultMuted
                  autoPlay={isActive}
                  loop
                  playsInline
                  preload={isActive ? 'auto' : 'metadata'}
                  poster={poster}
                  src={mediaUrl(slide.src)}
                />
              )}
            </div>
          );
        })}
        <div className="hero-scrim" />
      </motion.div>

      <div className="container hero-inner">
        <motion.div
          className="hero-copy"
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="hero-brand">
            LogitsLab<span className="logo-dot">.</span>
          </p>
          <h1 className="hero-title">AI that ships in production.</h1>
          <p className="hero-lede">
            Vision, language, and multimodal systems built for real cameras,
            real latency, and operators who need results—not demos that die in a notebook.
          </p>
          <div className="hero-actions">
            <button type="button" className="btn btn-primary" onClick={onContactClick}>
              Consult with us
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/portfolio')}
            >
              View our work
            </button>
          </div>
        </motion.div>

        <div className="hero-carousel-ui" role="group" aria-label="Demo reel">
          <AnimatePresence mode="wait">
            <motion.span
              key={active.id}
              className="hero-slide-label"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {active.label}
            </motion.span>
          </AnimatePresence>

          <div
            className="hero-carousel-controls"
            onTouchStart={(e) => {
              e.stopPropagation();
              setPaused(true);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setPaused(false);
            }}
          >
            <button
              type="button"
              className="hero-nav-btn"
              aria-label="Previous video"
              onClick={() => goRelative(-1)}
            >
              <FaChevronLeft size={12} />
            </button>
            <div className="hero-dots">
              {HERO_SLIDES.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`hero-dot ${i === index ? 'active' : ''}`}
                  aria-label={`Show ${slide.label}`}
                  aria-current={i === index ? 'true' : undefined}
                  onClick={() => goAbsolute(i)}
                />
              ))}
            </div>
            <button
              type="button"
              className="hero-nav-btn"
              aria-label="Next video"
              onClick={() => goRelative(1)}
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
